import React, { useEffect, useState } from 'react'; 
import { getRequest, getBaseUrl } from '../UrlRequest';
import { useLocation } from 'react-router-dom';
import Modal from 'react-modal';
import Loading from '../common/Loading';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import ko from 'date-fns/locale/ko';

const Diet = ({ patientId }) => {
  const [dietData, setDietData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedMeal, setSelectedMeal] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [monthlyMealDates, setMonthlyMealDates] = useState([]);
  const location = useLocation();
  const application = location.state?.application || 'hearton';

  const openModal = (meal) => {
    setSelectedMeal(meal);
  };

  const closeModal = () => {
    setSelectedMeal(null);
  };

  const fetchMealImage = async (mealId, token) => {
    try {
      const response = await fetch(`${getBaseUrl(application)}/doctor/meal-image/${mealId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) throw new Error('이미지를 불러오지 못했습니다.');
      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      console.error(`Meal ID ${mealId} 이미지 오류:`, error);
      return null;
    }
  };

  const fetchDietData = async (date) => {
    setDietData(null);
    setError(null);
    setLoading(true);
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setError('인증 토큰이 없습니다.');
        setLoading(false);
        return;
      }
      const data = await getRequest(
        application,
        `/doctor/${patientId}/meals?on-date=${date.toISOString().split('T')[0]}`,
        token
      );
      if (data && data.meals && data.meals.length > 0) {
        const mealsWithImages = await Promise.all(
          data.meals.map(async (meal) => {
            const imageUrl = await fetchMealImage(meal.meal_id, token);
            return { ...meal, imageUrl };
          })
        );
        setDietData(mealsWithImages);
      } else {
        setError('해당 날짜에 식단 데이터가 없습니다.');
      }
    } catch (error) {
      setError('식단 데이터를 불러오지 못했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const fetchMonthlyMealDates = async (date) => {
    try {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        setError('인증 토큰이 없습니다.');
        return;
      }
      
      const year = date.getFullYear();
      const month = date.getMonth() + 1;
      
      const data = await getRequest(
        application,
        `/doctor/${patientId}/meal/record?year=${year}&month=${month}`,
        token
      );
      
      if (data && data.recorded_list) {
        const mealDates = Object.entries(data.recorded_list)
          .filter(([_, hasData]) => hasData)
          .map(([dateStr]) => {
            const [year, month, day] = dateStr.split('-').map(Number);
            return new Date(year, month - 1, day);
          });
        setMonthlyMealDates(mealDates);
      }
    } catch (error) {
      console.error('월별 식단 데이터 조회 실패:', error);
    }
  };

  const handleDateChange = (date) => {
    setSelectedDate(date);
    fetchDietData(date);
  };

  const handleMonthChange = (date) => {
    fetchMonthlyMealDates(date);
  };

  useEffect(() => {
    if (patientId) {
      fetchMonthlyMealDates(selectedDate);
    }
  }, [patientId]);

  useEffect(() => {
    if (patientId) {
      fetchDietData(selectedDate);
    }
  }, [patientId, selectedDate]);

  return (
    <div style={{ 
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      gap: '20px' 
    }}>
      <div style={{ 
        flex: '0 0 auto',
        position: 'sticky',
        top: 0,
        width: '100%',
        backgroundColor: 'white',
        zIndex: 1000
      }}>
        <div style={{
          position: 'relative',
          width: '100%'
        }}>
          <DatePicker
            selected={selectedDate}
            onChange={handleDateChange}
            onMonthChange={handleMonthChange}
            locale={ko}
            dateFormat="yyyy년 MM월 dd일"
            showMonthYearPicker={false}
            formatWeekDay={nameOfDay => nameOfDay.substring(0, 1)}
            highlightDates={monthlyMealDates}
            inline
            className="custom-datepicker"
            calendarClassName="custom-calendar"
            dayClassName={date => {
              return monthlyMealDates.some(mealDate => 
                date.getFullYear() === mealDate.getFullYear() &&
                date.getMonth() === mealDate.getMonth() &&
                date.getDate() === mealDate.getDate()
              ) ? 'highlighted-date' : undefined;
            }}
            renderCustomHeader={({
              date,
              changeYear,
              changeMonth,
              decreaseMonth,
              increaseMonth,
              prevMonthButtonDisabled,
              nextMonthButtonDisabled
            }) => (
              <div
                style={{
                  margin: 10,
                  display: "flex",
                  justifyContent: "center",
                  fontSize: "1.5rem"
                }}
              >
                <button 
                  onClick={decreaseMonth} 
                  disabled={prevMonthButtonDisabled}
                  style={{ fontSize: "1.5rem", padding: "0 10px" }}
                >
                  {"<"}
                </button>
                <div style={{ margin: "0 10px" }}>
                  {date.getFullYear()}년 {date.getMonth() + 1}월
                </div>
                <button 
                  onClick={increaseMonth} 
                  disabled={nextMonthButtonDisabled}
                  style={{ fontSize: "1.5rem", padding: "0 10px" }}
                >
                  {">"}
                </button>
              </div>
            )}
            customInput={
              <input
                style={{
                  padding: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ced4da',
                  width: '100%',
                  fontSize: '1.5rem'
                }}
              />
            }
            wrapperClassName="datepicker-wrapper"
          />
          <style>
            {`
              .datepicker-wrapper {
                width: 100%;
              }
              .custom-calendar {
                width: 100% !important;
                font-size: 1.5rem !important;
              }
              .custom-calendar .react-datepicker__month-container {
                width: 100%;
              }
              .custom-calendar .react-datepicker__month {
                width: 100%;
              }
              .custom-calendar .react-datepicker__week {
                display: flex;
                justify-content: space-around;
              }
              .custom-calendar .react-datepicker__day-names {
                display: flex;
                justify-content: space-around;
              }
              .custom-calendar .react-datepicker__day {
                font-size: 1.5rem !important;
                line-height: 2rem !important;
                width: 2rem !important;
                margin: 0.5rem !important;
              }
              .custom-calendar .react-datepicker__day-name {
                font-size: 1.5rem !important;
                width: 2rem !important;
                line-height: 2rem !important;
                margin: 0.5rem !important;
              }
            `}
          </style>
        </div>
      </div>

      <div style={{ 
        flex: '1 1 auto',
        overflowY: 'auto',
        paddingRight: '20px'
      }}>
        {loading ? (
          <Loading message="식단 데이터 로딩 중..." size="medium" />
        ) : error ? (
          <p>{error}</p>
        ) : dietData ? (
          <>
            <h3 style={{ 
              color: '#2c3e50', 
              marginTop: '0',
              marginBottom: '20px', 
              fontSize: '1.25rem', 
              fontWeight: '600' 
            }}>식단</h3>
            <div style={{ 
              display: 'flex', 
              flexDirection: 'column',
              gap: '15px'
            }}>
              {dietData.map((meal) => (
                <div 
                  key={meal.meal_id} 
                  style={{ 
                    display: 'flex', 
                    border: '1px solid #e9ecef',
                    borderRadius: '8px',
                    padding: '12px',
                    backgroundColor: '#fff',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.2s ease',
                    maxHeight: '120px',
                  }}
                >
                  {meal.imageUrl && (
                    <img
                      src={meal.imageUrl}
                      alt={`${meal.meal_type} 이미지`}
                      style={{ 
                        width: 'calc(20% - 20px)', 
                        height: 'calc(20% - 20px)',
                        maxHeight: '140px',
                        maxWidth: '140px', 
                        marginRight: '20px', 
                        cursor: 'pointer',
                        borderRadius: '6px',
                        objectFit: 'cover'
                      }}
                      onClick={() => openModal(meal)}
                    />
                  )}
                  <div>
                    <h4 style={{ 
                      color: '#495057',
                      marginBottom: '10px',
                      fontSize: '1.1rem',
                      fontWeight: '600'
                    }}>{meal.meal_type}</h4>
                    <p style={{ 
                      color: '#495057',
                      marginBottom: '5px',
                      fontSize: '0.95rem'
                    }}>음식: {meal.menus[0]?.name}</p>
                    <p style={{ 
                      color: '#495057',
                      fontSize: '0.95rem'
                    }}>칼로리: {meal.menus[0]?.calorie} kcal</p>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          <p>{selectedDate.toISOString().split('T')[0]}에 대한 식단 데이터가 없습니다.</p>
        )}
      </div>

      <style>
        {`
          .highlighted-date {
            background-color: #4CAF50 !important;
            border-radius: 50%;
            color: white !important;
            font-weight: bold;
          }
          .highlighted-date:hover {
            background-color: #45a049 !important;
          }
          .react-datepicker {
            font-family: 'Arial', sans-serif;
            border: 1px solid #e0e0e0;
            border-radius: 8px;
            box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          }
          .react-datepicker__header {
            background-color: #f8f9fa;
            border-bottom: 1px solid #e0e0e0;
            border-top-left-radius: 8px;
            border-top-right-radius: 8px;
            padding-top: 10px;
          }
          .react-datepicker__day {
            margin: 0.2rem;
            width: 2rem;
            height: 2rem;
            line-height: 2rem;
            border-radius: 50%;
            transition: all 0.2s ease-in-out;
          }
          .react-datepicker__day:hover {
            background-color: #e9ecef;
          }
          .react-datepicker__day--selected {
            background-color: #007bff !important;
            color: white !important;
          }
          .react-datepicker__day--keyboard-selected {
            background-color: #007bff !important;
            color: white !important;
          }
        `}
      </style>

      {selectedMeal && (
        <Modal
          isOpen={!!selectedMeal}
          onRequestClose={closeModal}
          style={{
            overlay: {
              backgroundColor: 'rgba(0, 0, 0, 0.75)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            },
            content: {
              position: 'relative',
              top: 'auto',
              left: 'auto',
              right: 'auto',
              bottom: 'auto',
              maxWidth: '90%',
              maxHeight: '90%',
              padding: '20px',
              borderRadius: '12px',
              border: '1px solid #e9ecef',
              backgroundColor: '#fff',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
            }
          }}
          contentLabel="식단 상세 정보"
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h4>{selectedMeal.meal_type} - 상세 정보</h4>
            <button onClick={closeModal} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ width: '24px', height: '24px', color: '#000000' }}
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {selectedMeal.imageUrl && (
            <img
              src={selectedMeal.imageUrl}
              alt={`${selectedMeal.meal_type} 이미지`}
              style={{ width: '100%', maxWidth: '500px', height: 'auto', marginBottom: '20px' }}
            />
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '15px' }}>
            {selectedMeal.menus.map((menu, index) => (
              <div key={index} style={{ border: '1px solid #ddd', padding: '10px', borderRadius: '8px', backgroundColor: '#f9f9f9' }}>
                <h5 style={{ marginBottom: '10px' }}>음식: {menu.name}</h5>
                <p>양: {menu.amount} {menu.unit}</p>
                <p>칼로리: {menu.calorie} kcal</p>
                <p>단백질: {menu.protein} g</p>
                <p>지방: {menu.fat} g</p>
                <p>탄수화물: {menu.carbohydrate} g</p>
                <p>나트륨: {menu.sodium} mg</p>
                <p>칼륨: {menu.potassium} mg</p>
                <p>인: {menu.phosphorus} mg</p>
              </div>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
};

export default Diet;
